import ApiClient from ".."

// TYPES
import { InvoiceStatus } from "../../types/Invoice"

// In DTOs
import InvoiceGetCoachListIn from "../../dtos/in/InvoiceGetCoachListIn"
import InvoiceGetListIn from "../../dtos/in/InvoiceGetListIn"
import InvoicePatchItemIn from "../../dtos/in/InvoicePatchItemIn"
import InvoicePatchNotesIn from "../../dtos/in/InvoicePatchNotesIn"
import InvoicePostItemIn from "../../dtos/in/InvoicePostItemIn"

// Out DTOs
import GenericOut from "../../dtos/out/GenericOut"
import InvoiceGetItemListOut from "../../dtos/out/InvoiceGetItemListOut"
import InvoiceGetListOut from "../../dtos/out/InvoiceGetListOut"
import InvoiceGetOut from "../../dtos/out/InvoiceGetOut"
import InvoicePatchStatusOut from "../../dtos/out/InvoicePatchStatusOut"

export class InvoiceEndpoints {
  constructor(private client: ApiClient) {}
  static readonly BASE_URL = "/invoice"

  /**
   * Deletes an invoice item.
   */
  async deleteItem(invoiceItemId: number) {
    return this.client.delete<GenericOut>(InvoiceEndpoints.BASE_URL, `/item/${invoiceItemId}`)
  }

  /**
   * Retrieves an invoice's details.
   */
  async get(invoiceId: number) {
    return this.client.get<InvoiceGetOut>(InvoiceEndpoints.BASE_URL, `/${invoiceId}`)
  }

  /**
   * Retrieves a list of invoices with filters, ordering, and pagination.
   */
  async getList(payload: InvoiceGetListIn) {
    return this.client.get<InvoiceGetListOut>(InvoiceEndpoints.BASE_URL, "/", payload)
  }

  /**
   * Retrieves a list of invoices for a coach with filters, ordering, and pagination.
   */
  async getListForCoach(coachId: number, payload: InvoiceGetCoachListIn) {
    return this.client.get<InvoiceGetListOut>(InvoiceEndpoints.BASE_URL, `/coach/${coachId}`, payload)
  }

  /**
   * Retrieves a list of open invoice items for a coach with filters, ordering, and pagination.
   */
  async getOpenItemListForCoach(coachId: number) {
    return this.client.get<InvoiceGetItemListOut>(InvoiceEndpoints.BASE_URL, `/coach/${coachId}/item/open`)
  }

  /**
   * Associates the specified invoice item to an open invoice.
   */
  async patchAssociateItem(invoiceItemId: number) {
    return this.client.patch<GenericOut>(InvoiceEndpoints.BASE_URL, `/item/${invoiceItemId}/associate`, null)
  }

  /**
   * Updates an invoice item.
   */
  async patchItem(invoiceItemId: number, payload: InvoicePatchItemIn) {
    return this.client.patch<GenericOut>(InvoiceEndpoints.BASE_URL, `/item/${invoiceItemId}`, payload)
  }

  /**
   * Updates the specified invoice note.
   */
  async patchNote(invoiceId: number, noteType: "audit" | "coach", payload: InvoicePatchNotesIn) {
    return this.client.patch<GenericOut>(InvoiceEndpoints.BASE_URL, `/${invoiceId}/notes/${noteType}`, payload)
  }

  /**
   * Updates an invoice item's status to specified status.
   */
  async patchItemStatus(invoiceItemId: number, status: "rejected" | "approved") {
    return this.client.patch<InvoicePatchStatusOut>(
      InvoiceEndpoints.BASE_URL,
      `/item/${invoiceItemId}/status/${status}`,
      null
    )
  }

  /**
   * Updates an invoice's status to specified status.
   */
  async patchStatus(invoiceId: number, status: InvoiceStatus) {
    return this.client.patch<InvoicePatchStatusOut>(InvoiceEndpoints.BASE_URL, `/${invoiceId}/status/${status}`, null)
  }

  /**
   * Creates a new custom invoice item for an invoice or coach.
   */
  async postItem(coachId: number, payload: InvoicePostItemIn) {
    return this.client.post<GenericOut>(InvoiceEndpoints.BASE_URL, `/coach/${coachId}/item`, payload)
  }
}
